// src/quote/QuoteScreen.tsx: /quote?t=<token>, the private page the owner sends a builder (docs/DECISIONS.md "The quote page (owner,
// 2026-10-25)"; docs/SPEC.md 16 and 17; worker/src/platform/quote.ts). It renders on app.homebuild.ai, where there is no builder, so
// src/main.tsx shows it before the normal boot and it uses no tenant. Layout F, "a full-height split" (owner, 2026-10-26): on a
// laptop the window splits edge to edge, a navy side that stays still (the plan with Change, Due Today, the held date, what is
// included, the guarantee) and the form beside it (Your Details, Payment, the agreement, the one button). On a phone or a narrow
// window the navy side is a band across the top with what's included behind a toggle, then the form. Every state of the link
// (opening, setting up, accepted, expired, a dead link) wears the same frame. The money rules are the Activate screen's (one plan
// line with Change, one Due Today, the payment fields, one button; the details in the tooltip). The payment fields are the team's
// own (src/team/PaymentForm.tsx) on the quote's SetupIntent.
//
// Opening the page only peeks (GET); the button confirms the payment method with Stripe, then one POST makes the person a team admin,
// starts the plan and answers the builder's own sign-in link, which the page follows after "Setting up {Company}…" (the form side
// shows it; the navy side stays). There is no thank-you page. An accepted quote offers Sign In, an expired one the questions line,
// a link that names nothing says so.
import { useEffect, useLayoutEffect, useRef, useState } from "preact/hooks";
import { currentAcceptance, type AgreementId } from "../../shared/agreements";
import { accentProperties } from "../../shared/accent";
import type { BillingAddress, BillingAmount, BillingFounding, QuotePeekResponse } from "../../shared/api";
import { isEmail, normalizeEmail } from "../../shared/format";
import { planForToggle, todayCharge, type PlanInterval } from "../../shared/pricing";
import { api } from "../lib/api";
import { ApiError } from "../lib/http";
import { AgreeBox, AgreementSheet } from "../team/AgreementSheet";
import { ByStripe, dueWords, money, PlanChoice, planOptions } from "../team/Billing";
import { PaymentForm, type PaymentFormHandle } from "../team/PaymentForm";
import { Shell } from "../platform/Shell";
import { Button, Field, Icon, InfoTip, Sheet, Spinner, TextInput } from "../ui";
import "../styles/team.css";

/** The team side's navy, never a builder's colour (design system A). */
const TEAM_NAVY = "#0B1522";

/** The token in the address (`?t=`), or null. */
export function tokenFromSearch(search: string): string | null {
  const t = new URLSearchParams(search).get("t");
  return t && t.length <= 200 ? t : null;
}

type View =
  | { kind: "loading" }
  | { kind: "quote"; quote: QuotePeekResponse }
  | { kind: "gone" }
  | { kind: "error" };

/** "November 24": the day a date falls on where the quote is meant (the builder's), without the year. */
function day(iso: string, timeZone: string): string {
  return new Intl.DateTimeFormat("en-US", { month: "long", day: "numeric", timeZone }).format(Date.parse(iso));
}

// ------------------------------------------------------------------------------------------ the frames

/** The short states (opening, accepted, expired, a dead link, an error): their words beside the navy side. */
function Frame(props: { children: preact.ComponentChildren }) {
  return (
    <Shell screen="quote-state" mid>
      <div class="qt-s-body">{props.children}</div>
    </Shell>
  );
}

function Title(props: { first: string; second?: string }) {
  return (
    <h1 class="cf-title">
      {props.first}
      {props.second ? <em>{props.second}</em> : null}
    </h1>
  );
}

function Questions(props: { email: string }) {
  return (
    <p class="cf-fine">
      Questions? <a href={`mailto:${props.email}`}>{props.email}</a>
    </p>
  );
}

// ------------------------------------------------------------------------------------------ the screen

export function QuoteScreen(props: { token?: string | null }) {
  const token = props.token === undefined ? tokenFromSearch(window.location.search) : props.token;
  const [view, setView] = useState<View>(token ? { kind: "loading" } : { kind: "gone" });
  const [again, setAgain] = useState(0);

  useLayoutEffect(() => {
    for (const [k, v] of accentProperties(TEAM_NAVY)) document.documentElement.style.setProperty(k, v);
    document.title = "Your Quote · homebuild";
  }, []);

  useEffect(() => {
    if (!token) return;
    const ctl = new AbortController();
    api
      .quotePeek(token, { signal: ctl.signal })
      .then((quote) => setView({ kind: "quote", quote }))
      .catch((e: unknown) => {
        if (e instanceof DOMException && e.name === "AbortError") return;
        setView(e instanceof ApiError && e.status === 404 ? { kind: "gone" } : { kind: "error" });
      });
    return () => ctl.abort();
  }, [token, again]);

  if (view.kind === "loading") {
    return (
      <Frame>
        <div class="cf-wait">
          <Spinner label="Opening your quote" />
        </div>
      </Frame>
    );
  }
  if (view.kind === "gone") {
    return (
      <Frame>
        <Title first="This link doesn't work." />
        <p class="cf-fine">Ask whoever sent it for a new one.</p>
      </Frame>
    );
  }
  if (view.kind === "error") {
    return (
      <Frame>
        <Title first="We couldn't load this page." />
        <Button size="lg" class="cf-go" onClick={() => window.location.reload()}>
          Reload
        </Button>
      </Frame>
    );
  }
  const q = view.quote;
  if (q.state === "accepted") {
    return (
      <Frame>
        <Title first="This quote was accepted." />
        {q.signinUrl ? (
          <Button size="lg" class="cf-go" href={q.signinUrl}>
            Sign In
          </Button>
        ) : null}
        <Questions email={q.contactEmail} />
      </Frame>
    );
  }
  if (q.state === "expired" || !q.prices || !token) {
    return (
      <Frame>
        <Title first="This quote has expired." />
        <Questions email={q.contactEmail} />
      </Frame>
    );
  }
  return <OpenQuote quote={q} token={token} reread={() => setAgain((n) => n + 1)} />;
}

// ------------------------------------------------------------------------------------------ the open quote

/** What the plan is, in words: "Founding" with $699 struck and $499 a month, billed yearly. */
function PlanLine(props: { quote: QuotePeekResponse; founding: BillingFounding; interval: PlanInterval; onChange: () => void }) {
  const o = planOptions(props.quote.prices!.amounts, props.founding).find((x) => x.interval === props.interval)!;
  return (
    <div class="qt-plan">
      <div class="qt-plan-t">
        <span class="qt-k">Plan</span>
        <b class="qt-plan-v">
          {o.mark ?? o.name}
          <span class="qt-dot" aria-hidden="true">
            ·
          </span>
          {o.was ? (
            <s class="qt-was">
              <span class="sr">was </span>
              {o.was}
            </s>
          ) : null}
          <span class="qt-rate">{o.price} a month</span>
        </b>
        <span class="qt-plan-s">
          <span>{o.billed.charAt(0).toUpperCase() + o.billed.slice(1)}</span>
        </span>
      </div>
      <button type="button" class="link-btn qt-change" onClick={props.onChange}>
        Change
      </button>
    </div>
  );
}

const INCLUDED = ["An inquiry form on your website", "A board for every family", "A brief before every call", "Your pipeline, with who's serious"] as const;

function Checks() {
  return (
    <ul class="qt-checks">
      {INCLUDED.map((t) => (
        <li key={t}>
          <Icon name="check" size="sm" />
          {t}
        </li>
      ))}
    </ul>
  );
}

function OpenQuote(props: { quote: QuotePeekResponse; token: string; reread: () => void }) {
  const q = props.quote;
  const token = props.token;
  const founding: BillingFounding = { market: null, spotsTotal: 10, spotsLeft: 10, offered: q.offer === "founding" };
  const amounts = q.prices!.amounts;
  const [interval, setInterval] = useState<PlanInterval>(founding.offered ? "year" : "month");
  const [name, setName] = useState(q.name);
  const [email, setEmail] = useState(q.email);
  const [agreed, setAgreed] = useState(false);
  const [doc, setDoc] = useState<AgreementId | null>(null);
  const [sheet, setSheet] = useState(false);
  const [open, setOpen] = useState(false);
  const [address, setAddress] = useState<BillingAddress | null>(null);
  const [taxed, setTaxed] = useState<{ interval: PlanInterval; value: BillingAmount } | null>(null);
  const [complete, setComplete] = useState(false);
  const [busy, setBusy] = useState(false);
  const [phase, setPhase] = useState<"form" | "setting">("form");
  const [formError, setFormError] = useState<string | null>(null);
  const [payError, setPayError] = useState<string | null>(null);
  const [formKey, setFormKey] = useState(0);
  const handle = useRef<PaymentFormHandle | null>(null);
  const confirmed = useRef<string | null>(null);
  const asked = useRef(0);
  const latest = useRef({ name, email });
  latest.current = { name: name.trim(), email: email.trim() };

  const plan = planForToggle(interval, founding.offered);
  const price = todayCharge(amounts, plan).total;
  const word = interval === "year" ? "year" : "month";
  const tax = q.tax;
  const amount = taxed && taxed.interval === interval ? taxed.value : null;
  const known = tax && amount !== null && amount.tax !== null;
  const first = q.firstCharge ? day(q.firstCharge, "UTC") : null;

  // With sales tax: the first period with tax for the address once it is complete (and has settled for a moment). The server prices it.
  useEffect(() => {
    const n = ++asked.current;
    if (!tax || !address) {
      setTaxed(null);
      return;
    }
    const timer = setTimeout(() => {
      api.quotePreview({ t: token, interval, address }).then(
        (value) => n === asked.current && setTaxed({ interval, value }),
        () => undefined,
      );
    }, 400);
    return () => clearTimeout(timer);
  }, [tax, address, interval]);

  const total = known ? amount!.total : price;
  const dueNow = first ? "$0" : money(total, "usd", true);
  const dueMore = !first && tax && !known ? "plus sales tax" : null;
  const freeLine = first ? `Free until ${first}, then ${known ? money(amount!.total, "usd", true) : money(price, "usd", true)} a ${word}${known ? "" : " plus sales tax"}.` : null;
  const taxName = address?.state === "NY" ? "New York sales tax" : "sales tax";
  const detail = first
    ? `Nothing is charged today. Your first payment is on ${first}, with sales tax for your address.`
    : known
      ? `Your first ${word}, ${money(price, "usd", true)}, plus ${money(amount!.tax!)} ${taxName}. Charged today.`
      : `Your first ${word}, ${money(price, "usd", true)}${tax ? ", plus sales tax for your address" : ""}. Charged today.`;
  const button = first ? "Get Started" : dueWords(price, amount, tax, "Get Started").button;
  const guarantee = first ? "30-day money-back guarantee from your first payment." : "30-day money-back guarantee.";
  const heldUntil = day(q.heldUntil, "America/New_York");

  const ready = agreed && name.trim().length > 0 && isEmail(normalizeEmail(email)) && complete && !!address;

  const press = async () => {
    if (busy || !ready) return;
    setBusy(true);
    setFormError(null);
    setPayError(null);
    try {
      let id = confirmed.current;
      if (!id) {
        const form = handle.current;
        if (!form) return setPayError("The payment form is still loading. Try again in a moment.");
        const out = await form.confirm();
        if (!out.ok) return setPayError(out.message);
        id = out.setupIntentId;
        confirmed.current = id;
      }
      setPhase("setting");
      const { next } = await api.quoteAccept({
        t: token, setupIntentId: id, name: name.trim(), email: normalizeEmail(email), interval, expectedTotal: price, agree: currentAcceptance(),
      });
      window.location.assign(next);
    } catch (e) {
      setPhase("form");
      if (!(e instanceof ApiError)) return setFormError("That didn't go through. Try again.");
      if (e.fields?.["quote"]) return props.reread();
      if (e.fields?.["payment"]) {
        // This payment method came to nothing: a new form (and its SetupIntent) takes the next one.
        confirmed.current = null;
        setFormKey((k) => k + 1);
        return setPayError(e.fields["payment"]);
      }
      setFormError(e.status === 400 && e.fields ? (Object.values(e.fields)[0] ?? e.message) : e.message);
    } finally {
      setBusy(false);
    }
  };


  const setting = phase === "setting";
  const sheetOpen = sheet && !setting;

  return (
    <Shell
      label="Your quote"
      screen={setting ? "quote-setting" : "quote"}
      inert={setting}
      side={
        <>
          <h1 class="sr">homebuild for {q.company}</h1>
          <PlanLine quote={q} founding={founding} interval={interval} onChange={() => setSheet(true)} />
          <div class="qt-due">
            <span class="qt-k">
              Due Today
              <InfoTip label="About the amount" align="start" onNavy>
                {detail}
              </InfoTip>
            </span>
            <b class="qt-due-v">{dueNow}</b>
            {freeLine ? <span class="qt-due-s">{freeLine}</span> : dueMore ? <span class="qt-due-s">{dueMore}</span> : null}
          </div>
          <p class="qt-held">Price held until {heldUntil}.</p>
          <button type="button" class="qt-tog" aria-expanded={open} onClick={() => setOpen(!open)}>
            What's included
            <Icon name="chev-d" size="sm" />
          </button>
          <div class={`qt-incl${open ? " is-open" : ""}`}>
            <Checks />
            <p class="qt-plus">Your whole team. No setup fee.</p>
            <p class="qt-guar">
              <Icon name="shield" size="sm" />
              {guarantee}
            </p>
          </div>
        </>
      }
    >
      <div class="qt-form" hidden={setting}>
        <section class="qt-sec" aria-labelledby="qt-det">
          <h2 class="qt-h" id="qt-det">
            Your Details
          </h2>
          <div class="qt-fields">
            <Field label="Your name">
              {(c) => <TextInput {...c} value={name} autoComplete="name" onInput={(e) => setName((e.target as HTMLInputElement).value)} />}
            </Field>
            <Field label="Work email">
              {(c) => <TextInput {...c} type="email" value={email} autoComplete="email" inputMode="email" onInput={(e) => setEmail((e.target as HTMLInputElement).value)} />}
            </Field>
            <div class="qt-co">
              <span class="qt-co-k">Company</span>
              <span class="qt-co-v">
                {q.logo ? <img src={q.logo.url} alt="" width="26" height="21" /> : null}
                {q.company}
              </span>
            </div>
          </div>
        </section>
        <section class="qt-sec" aria-labelledby="qt-pay">
          <h2 class="qt-h" id="qt-pay">
            Payment
          </h2>
          <PaymentForm
            key={formKey}
            purpose="start"
            open={() => api.quoteSetup(token)}
            company={q.company}
            contact={() => ({ name: latest.current.name, email: normalizeEmail(latest.current.email) })}
            address={null}
            onHandle={(h) => (handle.current = h)}
            onComplete={setComplete}
            onAddress={setAddress}
          />
          {payError ? (
            <p class="bill-err" role="alert">
              {payError}
            </p>
          ) : null}
        </section>
        <div class="qt-fin">
          <AgreeBox checked={agreed} onChange={setAgreed} onOpen={setDoc} />
          {formError ? (
            <p class="qt-err" role="alert">
              {formError}
            </p>
          ) : null}
          <Button size="lg" class="qt-go" disabled={!ready} busy={busy} onClick={() => void press()}>
            {button}
          </Button>
          <ByStripe />
          <p class="qt-q">
            Questions? <a href={`mailto:${q.contactEmail}`}>{q.contactEmail}</a>
          </p>
        </div>
        <AgreementSheet doc={doc} onClose={() => setDoc(null)} />
      </div>
      {setting ? (
        <div class="qt-setting" role="status">
          <Spinner label="Setting up your workspace" />
          <h2 class="qt-setting-h">Setting up {q.company}…</h2>
        </div>
      ) : null}
      <Sheet open={sheetOpen} onClose={() => setSheet(false)} title="Change Plan" variant="modal" width={680} class="bill-sheet" footer={<Button onClick={() => setSheet(false)}>Done</Button>}>
        <PlanChoice amounts={amounts} founding={founding} interval={interval} onInterval={setInterval} tax={tax || !!first} due={amount} when={first ? `on ${first}` : undefined} />
      </Sheet>
    </Shell>
  );
}
